import React from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { IoDownloadOutline } from "react-icons/io5";
import { useFormik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import { uploadBulkProducts } from "../../../api/context/products";

const BulkUploadModal = ({
  show,
  onHide,
  onSuccess,
  sampleFile,
  sampleImageFile
}) => {
  const [bulkLoading, setBulkLoading] = React.useState(false);

  const handleExcelFileChange = (event) => {
    const file = event.currentTarget.files[0];
    const supportedFormats = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    if (file && !supportedFormats.includes(file.type)) {
      toast.error('Unsupported file format. Please upload an Excel file.');
      return;
    }
    bulkFormikProduct.setFieldValue('excelFile', file);
  };

  const handleImageZipChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file && !file.type.includes('zip') && !file.name.endsWith('.zip')) {
      toast.error('Please upload a valid ZIP file.');
      return;
    }
    bulkFormikProduct.setFieldValue('imageZip', file);
  };

  const bulkFormikProduct = useFormik({
    initialValues: {
      excelFile: null,
      imageZip: null
    },
    validationSchema: yup.object().shape({
      excelFile: yup
        .mixed()
        .required("Excel file is required")
        .test(
          "fileFormat",
          "Unsupported file format",
          (value) => value && [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
          ].includes(value.type)
        ),
      imageZip: yup
        .mixed()
        .nullable()
        .test(
          "fileFormat",
          "Only ZIP files are allowed",
          (value) => !value || value.type.includes('zip') || value.name.endsWith('.zip')
        )
    }),
    onSubmit: async (values, { resetForm }) => {
      setBulkLoading(true);
      try {
        let formData = new FormData();
        formData.append("excelFile", values.excelFile);

        if (values.imageZip) {
          formData.append("imageZip", values.imageZip);
        }

        uploadBulkProducts(formData)((response) => {
          if (response?.data?.statusCode === 200) {
            setBulkLoading(false);
            onHide();
            resetForm();
            onSuccess();
            toast.success("Products uploaded successfully");
          } else {
            setBulkLoading(false);
            toast.error(response?.data?.message || "Upload failed");
          }
        });
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong! Please try again");
        setBulkLoading(false);
      }
    },
  });

  const downloadSampleFile = () => {
    const a = document.createElement("a");
    a.href = sampleFile;
    a.download = `sample-sheet.xlsx`;
    document.body.appendChild(a);
    a.click();
  };

  const downloadSampleFileImage = () => {
    const a = document.createElement("a");
    a.href = sampleImageFile;
    a.download = `images.zip`;
    document.body.appendChild(a);
    a.click();
  };

  const handleClose = () => {
    bulkFormikProduct.resetForm();
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="modal_create"
      size="xxl"
    >
      <Modal.Header closeButton className="border-0 pb-20">
        <Modal.Title className="fw-bold">Bulk Inventory Upload</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-30">
        <div className="modal_content_wrapper">
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-dark mb-0">Upload Product Excel File</h6>
              <button
                onClick={downloadSampleFile}
                className="text-decoration-none bg-transparent border-0 p-0 d-flex align-items-center gap-1"
              >
                <IoDownloadOutline className="text-black" size={16} />
                <span className="text-black" style={{ textDecoration: 'underline' }}>Download Sample</span>
              </button>
            </div>
            <div className="border rounded p-3 bg-light">
              <Form.Group controlId="formExcelFile">
                <Form.Control
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleExcelFileChange}
                  isInvalid={bulkFormikProduct.touched.excelFile && bulkFormikProduct.errors.excelFile}
                />
                <Form.Control.Feedback type="invalid">
                  {bulkFormikProduct.errors.excelFile}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center my-3">
              <h6 className="text-dark mb-0">Upload Product Images</h6>
              <button
                onClick={downloadSampleFileImage}
                className="text-decoration-none bg-transparent border-0 p-0 d-flex align-items-center gap-1"
              >
                <IoDownloadOutline className="text-black" size={16} />
                <span className="text-black" style={{ textDecoration: 'underline' }}>Download Sample</span>
              </button>
            </div>
            <div className="border rounded p-3 bg-light">
              <Form.Group controlId="formImageZip">
                <Form.Control
                  type="file"
                  accept=".zip"
                  onChange={handleImageZipChange}
                  isInvalid={bulkFormikProduct.touched.imageZip && bulkFormikProduct.errors.imageZip}
                />
                <Form.Control.Feedback type="invalid">
                  {bulkFormikProduct.errors.imageZip}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button className='cancel_button' variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          className="request_btn"
          variant="primary"
          onClick={bulkFormikProduct.handleSubmit}
          disabled={!bulkFormikProduct.values.excelFile || bulkLoading}
        >
          {bulkLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">Uploading...</span>
            </>
          ) : (
            "Upload Products"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkUploadModal;